import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Input, Select } from 'antd';
import type { TAssessmentItem } from 'types/operation';
import {
    addOrUpdateAssessment,
    duplicateAssessment,
} from 'api/operation/assessment';
import {
    EPageStatus,
} from 'types/helloKlarity';
import commonS from 'styles/common.module.less';
import s from './s.module.less';
import { EFeatureType } from 'constants/textVisit';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { Meta } from 'types/common';

type IProps = {
    item?: TAssessmentItem;
    itemStatus: EPageStatus;
    onCancel: () => void;
    onSuccess: () => void;
};

const AssessmentModal: React.FC<IProps> = (props) => {
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<TAssessmentItem & { active: boolean }>();
    const { item, itemStatus, onCancel, onSuccess } = props;
    const [setting] = useSettings();

    const helloKlaritySpecialtyOptions = (setting?.[EnumFields.HELLO_KLARITY_SPECIALTY] || []) as Meta[];
    const appointmentTypeOptions = (setting?.[EnumFields.HELLO_KLARITY_SERVICE_TYPE] || []) as Meta[];

    const titleMap = {
        [EPageStatus.ADD]: 'Add a new workflow',
        [EPageStatus.EDIT]: 'Workflow information',
        [EPageStatus.DUPLICATE]: 'Duplicate an workflow',
    };

    const onSave = async () => {
        setSubmitting(true);
        try {
            const formValues = await form.validateFields();
            const id = item?.id;

            const params = {
                assessmentName: formValues.assessmentName,
                partnerName: formValues.partnerName,
                specialty: (formValues as any).specialty || undefined,
                serviceType: (formValues as any).serviceType || undefined,
                promptManagement: item?.promptManagement || '',
                status: item?.status || 'active',
                featureType: EFeatureType.INTAKE_FORM_WORKFLOW,
                tags: [],
            } as TAssessmentItem & { specialty?: string; serviceType?: string };
            if (itemStatus === EPageStatus.EDIT) {
                params.id = id;
            }

            const handleDataFun =
              itemStatus === EPageStatus.DUPLICATE
                  ? duplicateAssessment({
                      id: item?.id || '',
                      ...params,
                  })
                  : addOrUpdateAssessment(params);
            const { data } = await handleDataFun;
            if (data.code === 200) {
                onSuccess?.();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (item) {
            const rawSpecialty = (item as any)?.specialty ?? (item as any)?.specialtyType ?? (item as any)?.specialties ?? '';
            const rawServiceType = (item as any)?.serviceType ?? (item as any)?.appointmentType ?? (item as any)?.serviceTypes ?? '';
            const specialtyValue = String(rawSpecialty || '');
            const serviceTypeValue = String(rawServiceType || '');

            if (EPageStatus.DUPLICATE === itemStatus) {
                form.setFieldsValue({
                    assessmentName: `Copy of ${item.assessmentName}`,
                    partnerName: item.partnerName,
                    specialty: specialtyValue,
                    serviceType: serviceTypeValue,
                } as any);
            } else {
                form.setFieldsValue({
                    assessmentName: item.assessmentName,
                    partnerName: item.partnerName,
                    specialty: specialtyValue,
                    serviceType: serviceTypeValue,
                } as any);
            }
        } else {
            form.resetFields();
        }
    }, [item, itemStatus, form]);
    return (
        <Modal
            title={titleMap[itemStatus]}
            open
            destroyOnClose
            okText="Save"
            onCancel={onCancel}
            footer={
                <div>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                        loading={submitting}
                        type="primary"
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className={s.wrap}>
                <Form
                    form={form}
                    className={commonS.formStyle1}
                    initialValues={{ active: true }}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                >
                    <>
                        <Form.Item
                            label="Assessment name"
                            name="assessmentName"
                            validateFirst
                            rules={[
                                {
                                    required: true,
                                    message: 'Assessment name is required',
                                },
                            ]}
                        >
                            <Input
                                maxLength={70}
                                showCount
                                placeholder="Ex. ADHD self assessment"
                            />
                        </Form.Item>
                        <Form.Item label="Specialty" name="specialty">
                            <Select
                                showSearch
                                virtual={false}
                                placeholder="Select specialty"
                                optionFilterProp="children"
                                allowClear
                            >
                                {helloKlaritySpecialtyOptions.map((opt) => (
                                    <Select.Option key={String(opt.label)} value={String(opt.label)}>
                                        {opt.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Appointment type" name="serviceType">
                            <Select
                                showSearch
                                virtual={false}
                                placeholder="Select appointment type"
                                optionFilterProp="children"
                                allowClear
                            >
                                {appointmentTypeOptions.map((opt) => (
                                    <Select.Option key={String(opt.label)} value={String(opt.label)}>
                                        {opt.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </>
                </Form>
            </div>
        </Modal>
    );
};

export default AssessmentModal;
